import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, FileText, CheckCircle2, Clock, AlertTriangle, Trash2, Calendar, Euro } from 'lucide-react'
import api from '../services/api'
import { useTheme } from '../hooks/useTheme'
import { useLanguage } from '../hooks/useLanguage'

interface F24Tributo {
  codice_tributo: string
  rateazione?: string
  periodo?: string
  importo_a_debito: number
  importo_a_credito: number
}

interface F24Inps {
  sede: string
  causale: string
  matricola?: string
  periodo_da?: string
  periodo_a?: string
  importo_a_debito: number
  importo_a_credito: number
}

interface F24Payment {
  id: string
  workspace_id: string
  filename: string
  scadenza: string
  rata: string | null
  codice_fiscale: string
  cognome: string
  nome: string
  totale_erario: number
  totale_inps: number
  totale_regioni: number
  totale_imu: number
  totale_altri_enti: number
  saldo_finale: number
  tributi_erario: F24Tributo[] | null
  tributi_inps: F24Inps[] | null
  tributi_regioni: F24Tributo[] | null
  tributi_imu: F24Tributo[] | null
  status: 'da_pagare' | 'pagato' | 'scaduto'
  paid_date: string | null
  created_at: string
  updated_at: string
}

interface F24Response {
  success: boolean
  payments: F24Payment[]
  summary: {
    total: number
    da_pagare: number
    pagato: number
    scaduto: number
    totale_da_pagare: number
  }
}

const statusConfig = {
  da_pagare: { label: 'Da pagare', labelEn: 'To pay', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', icon: Clock },
  pagato: { label: 'Pagato', labelEn: 'Paid', color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', icon: CheckCircle2 },
  scaduto: { label: 'Scaduto', labelEn: 'Overdue', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', icon: AlertTriangle },
}

export default function F24() {
  const { isDark } = useTheme()
  const { language } = useLanguage()
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<'all' | 'da_pagare' | 'pagato' | 'scaduto'>('all')

  const { data, isLoading, error } = useQuery<F24Response>({
    queryKey: ['f24-payments'],
    queryFn: async () => {
      const response = await api.get('/f24')
      return response.data
    },
    retry: 1,
    staleTime: 60_000,
  })

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      await api.patch(`/f24/${id}/status`, { status })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['f24-payments'] })
      queryClient.invalidateQueries({ queryKey: ['fiscal-deadlines'] })
    },
  })

  const deleteF24 = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/f24/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['f24-payments'] })
      queryClient.invalidateQueries({ queryKey: ['fiscal-deadlines'] })
    },
  })

  const payments = useMemo(() => {
    if (!data?.payments) return []
    if (filter === 'all') return data.payments
    return data.payments.filter(p => p.status === filter)
  }, [data, filter])

  const formatDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-')
    return `${d}/${m}/${y}`
  }

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount)
  }

  const getDaysUntil = (dateStr: string) => {
    const target = new Date(dateStr)
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    target.setHours(0, 0, 0, 0)
    return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className={`rounded-lg border p-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <p className="text-red-500">{language === 'it' ? 'Errore nel caricamento dei pagamenti F24' : 'Error loading F24 payments'}</p>
      </div>
    )
  }

  const summary = data?.summary

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'it' ? 'Pagamenti F24' : 'F24 Payments'}
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {language === 'it' ? 'Gestisci le deleghe di pagamento F24 caricate' : 'Manage uploaded F24 payment forms'}
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className={`rounded-lg border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <p className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {language === 'it' ? 'Totale F24' : 'Total F24'}
            </p>
            <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{summary.total}</p>
          </div>
          <div className={`rounded-lg border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <p className="text-xs font-medium uppercase tracking-wide text-amber-500">
              {language === 'it' ? 'Da pagare' : 'To pay'}
            </p>
            <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{summary.da_pagare}</p>
          </div>
          <div className={`rounded-lg border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <p className="text-xs font-medium uppercase tracking-wide text-green-500">
              {language === 'it' ? 'Pagati' : 'Paid'}
            </p>
            <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{summary.pagato}</p>
          </div>
          <div className={`rounded-lg border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <p className="text-xs font-medium uppercase tracking-wide text-red-500">
              {language === 'it' ? 'Importo da saldare' : 'Amount due'}
            </p>
            <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatAmount(summary.totale_da_pagare)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        {(['all', 'da_pagare', 'pagato', 'scaduto'] as const).map(f => {
          const labels: Record<string, { it: string; en: string }> = {
            all: { it: 'Tutti', en: 'All' },
            da_pagare: { it: 'Da pagare', en: 'To pay' },
            pagato: { it: 'Pagati', en: 'Paid' },
            scaduto: { it: 'Scaduti', en: 'Overdue' },
          }
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                filter === f
                  ? 'bg-blue-600 text-white'
                  : isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {language === 'it' ? labels[f].it : labels[f].en}
            </button>
          )
        })}
      </div>

      {/* Payments List */}
      {payments.length === 0 ? (
        <div className={`rounded-lg border p-8 text-center ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <FileText className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            {language === 'it' ? 'Nessun F24 trovato' : 'No F24 found'}
          </p>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {language === 'it'
              ? 'Carica un PDF F24 dalla pagina Upload per iniziare'
              : 'Upload an F24 PDF from the Upload page to get started'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {payments.map(payment => {
            const cfg = statusConfig[payment.status]
            const StatusIcon = cfg.icon
            const daysUntil = getDaysUntil(payment.scadenza)
            const isUrgent = payment.status === 'da_pagare' && daysUntil <= 7 && daysUntil >= 0

            return (
              <div
                key={payment.id}
                className={`rounded-lg border p-4 transition-all ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} ${isUrgent ? 'ring-2 ring-red-400/50' : ''}`}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Left: info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                        <StatusIcon className="w-3 h-3" />
                        {language === 'it' ? cfg.label : cfg.labelEn}
                      </span>
                      {payment.rata && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                          {payment.rata}
                        </span>
                      )}
                      {isUrgent && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-medium">
                          ⚠️ {daysUntil === 0 ? (language === 'it' ? 'Scade oggi!' : 'Due today!') : `${daysUntil}g`}
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <Calendar className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          {formatDate(payment.scadenza)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Euro className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatAmount(payment.saldo_finale)}
                        </span>
                      </div>
                    </div>

                    {/* Sections breakdown */}
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                      {payment.totale_erario > 0 && (
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Erario: {formatAmount(payment.totale_erario)}
                        </span>
                      )}
                      {payment.totale_inps > 0 && (
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          INPS: {formatAmount(payment.totale_inps)}
                        </span>
                      )}
                      {payment.totale_regioni > 0 && (
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Regioni: {formatAmount(payment.totale_regioni)}
                        </span>
                      )}
                      {payment.totale_imu > 0 && (
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          IMU: {formatAmount(payment.totale_imu)}
                        </span>
                      )}
                    </div>

                    <p className={`text-xs mt-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {payment.cognome} {payment.nome} — CF: {payment.codice_fiscale}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                      {payment.filename}
                    </p>
                  </div>

                  {/* Right: actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    {payment.status === 'da_pagare' && (
                      <button
                        onClick={() => updateStatus.mutate({ id: payment.id, status: 'pagato' })}
                        disabled={updateStatus.isPending}
                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
                        title={language === 'it' ? 'Segna come pagato' : 'Mark as paid'}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 inline-block mr-1" />
                        {language === 'it' ? 'Pagato' : 'Paid'}
                      </button>
                    )}
                    {payment.status === 'pagato' && (
                      <button
                        onClick={() => updateStatus.mutate({ id: payment.id, status: 'da_pagare' })}
                        disabled={updateStatus.isPending}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        title={language === 'it' ? 'Ripristina' : 'Undo'}
                      >
                        {language === 'it' ? 'Ripristina' : 'Undo'}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (confirm(language === 'it' ? 'Eliminare questo F24?' : 'Delete this F24?')) {
                          deleteF24.mutate(payment.id)
                        }
                      }}
                      disabled={deleteF24.isPending}
                      className={`p-1.5 rounded-md transition-colors ${isDark ? 'text-gray-500 hover:text-red-400 hover:bg-gray-700' : 'text-gray-400 hover:text-red-500 hover:bg-gray-100'}`}
                      title={language === 'it' ? 'Elimina' : 'Delete'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
